import { useEffect, useRef, useState } from 'react'
import { Document, Page } from '../pdf/pdfjs'
import { loadStarterPreview } from './starterPreviewCache'
import { colors, fontSize } from '../../styles/theme'

const THUMB_WIDTH = 220

/** First page of a starter's preview PDF, fetched once the card scrolls into view. */
export default function StarterThumbnail({ starterKey, name }: { starterKey: string; name: string }) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) { setVisible(true); io.disconnect() }
    }, { rootMargin: '200px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!visible) return
    let cancelled = false
    loadStarterPreview(starterKey)
      .then(b => { if (!cancelled) setBlob(b) })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [visible, starterKey])

  const placeholder = (text: string) => (
    <div style={{ color: colors.textMuted, fontSize: fontSize.xs }}>{text}</div>
  )

  return (
    <div
      ref={ref}
      data-testid={`starter-thumb-${starterKey}`}
      aria-label={`${name} preview`}
      role="img"
      style={{
        aspectRatio: '8.5 / 11', width: '100%', overflow: 'hidden',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        background: colors.background, borderBottom: `1px solid ${colors.border}`,
      }}
    >
      {failed ? placeholder('Preview unavailable')
        : !blob ? <div style={{ alignSelf: 'center' }}>{placeholder(visible ? 'Loading…' : '')}</div>
        : (
          <Document file={blob} loading={null} error={placeholder('Preview unavailable')}>
            <Page pageNumber={1} width={THUMB_WIDTH} renderTextLayer={false} renderAnnotationLayer={false} loading={null} />
          </Document>
        )}
    </div>
  )
}
